import React, { useState, useEffect } from 'react';
import { ArrowLeft, Send, CheckCircle, AlertCircle, Users, ExternalLink, MessageSquare, Calendar, Clock, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import functions from components.js
import { 
  sendPetitionEventWebhook, 
  isGovernmentMinister, 
  canAddGovernmentResponse, 
  canEditDebateInfo, 
  canDeletePetitions, 
  canManageTerms,
  GovernmentAdminPanel,
  getUserAvatar,
  isBritishCitizen,
  hasUserSignedPetition,
  getAllPetitions,
  signPetition,
  unsignPetition,
  getCurrentTerm,
  formatTermDisplay,
  getPetitionsByTerm,
  getParliamentaryTerms,
  canRemoveGovernmentResponse,
  canRemoveDebateInfo,
  GOVERNMENT_POSITIONS,
  deletePetition
} from './components';

// Import sendPetitionEventWebhook from components.js (no need for webhook URL here)

const RESPOND_THRESHOLD = 10;
const DEBATE_THRESHOLD = 20;
const CLOSE_WEEKS = 3;
const WATCH_URL = 'https://www.roblox.com/games/118623703914061/Palace-of-Westminster';

const formatLongDate = (d) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// Expandable government response body used inside the progress timeline
const GovResponseBody = ({ text }) => {
  const [open, setOpen] = useState(false);
  const isLong = (text || '').length > 240;
  const shown = open || !isLong ? text : text.slice(0, 240) + '…';
  return (
    <div>
      <p className="italic text-gray-700">"{shown}"</p>
      {isLong && (
        <button
          onClick={() => setOpen(!open)}
          className="text-blue-600 hover:text-blue-800 underline mt-1"
          data-testid="toggle-full-response"
        >
          {open ? 'Show less' : 'Read full response'}
        </button>
      )}
    </div>
  );
};

// Build the list of progress events (most recent first)
const buildProgressEvents = (petition, signatureCount) => {
  const created = petition.created || petition.createdAt;
  const closeDate = new Date(new Date(created).getTime() + CLOSE_WEEKS * 7 * 24 * 60 * 60 * 1000);
  const events = [];

  events.push({
    key: 'published',
    rank: 1,
    date: new Date(created),
    title: 'Petition published',
    body: (
      <div className="space-y-2">
        <p>This petition can now be signed.</p>
        <p>If this petition gets {RESPOND_THRESHOLD} signatures, the government will respond to it.</p>
        <p>If this petition gets {DEBATE_THRESHOLD} signatures, it will be considered for a debate in Parliament.</p>
        <p>This petition will close on <span className="font-semibold">{formatLongDate(closeDate)}</span> if it does not reach the minimum of {RESPOND_THRESHOLD} signatures.</p>
      </div>
    ),
  });

  if (petition.respondThresholdAt || signatureCount >= RESPOND_THRESHOLD) {
    events.push({
      key: 'respond-threshold',
      rank: 2,
      date: new Date(petition.respondThresholdAt || created),
      title: 'Government will respond to this petition',
      body: <p>This petition has received {RESPOND_THRESHOLD} signatures and is now awaiting a government response</p>,
    });
  }

  if (petition.debateThresholdAt || signatureCount >= DEBATE_THRESHOLD) {
    events.push({
      key: 'debate-threshold',
      rank: 4,
      date: new Date(petition.debateThresholdAt || created),
      title: 'This petition will be considered for debate in Parliament',
      body: <p>This petition has received {DEBATE_THRESHOLD} signatures and is now being considered for a debate in Parliament</p>,
    });
  }

  if (petition.governmentResponse) {
    const text = typeof petition.governmentResponse === 'object'
      ? (petition.governmentResponse.text || petition.governmentResponse.content || '')
      : petition.governmentResponse;
    events.push({
      key: 'gov-responded',
      rank: 3,
      date: new Date(petition.governmentResponseMeta?.addedAt || created),
      title: 'Government responded to this petition',
      body: (
        <div>
          <GovResponseBody text={text} />
          {petition.governmentResponseMeta?.addedBy && (
            <p className="mt-4 text-gray-700">{petition.governmentResponseMeta.addedBy} - {petition.governmentResponseMeta.addedByRole}</p>
          )}
        </div>
      ),
    });
  }

  const di = petition.debateInfo;
  if (di && (di.status === 'scheduled' || di.status === 'debated') && di.date) {
    // The announcement is always made on/before the debate date. addedAt can be
    // overwritten to the completion time when a debate is marked debated, so cap it.
    const announced = new Date(petition.debateInfoMeta?.addedAt || di.date);
    const debateDay = new Date(di.date);
    events.push({
      key: 'will-debate',
      rank: 5,
      date: announced <= debateDay ? announced : debateDay,
      title: `Parliament will debate this petition on ${formatLongDate(di.date)}`,
      body: (
        <div>
          <p>
            You'll be able to watch in-game at{' '}
            <a href={WATCH_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">{WATCH_URL}</a>.
          </p>
          {petition.debateInfoMeta?.addedBy && (
            <p className="mt-4 text-gray-700">{petition.debateInfoMeta.addedBy} - {petition.debateInfoMeta.addedByRole}</p>
          )}
        </div>
      ),
    });
  }

  if (di && di.status === 'debated') {
    const hansard = di.hansardUrl || di.hansardLink;
    events.push({
      key: 'debated',
      rank: 6,
      date: new Date(di.date),
      title: 'Parliament debated this petition',
      body: (
        <div>
          {hansard
            ? <a href={hansard} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline" data-testid="read-hansard-link">Read the hansard of this debate</a>
            : <p className="text-gray-500">The Hansard record will be available soon.</p>}
          {(petition.debateInfoMeta?.editedBy || petition.debateInfoMeta?.addedBy) && (
            <p className="mt-4 text-gray-700">{petition.debateInfoMeta.editedBy || petition.debateInfoMeta.addedBy} - {petition.debateInfoMeta.editedByRole || petition.debateInfoMeta.addedByRole}</p>
          )}
        </div>
      ),
    });
  }

  if (di && di.status === 'cancelled') {
    events.push({
      key: 'debate-cancelled',
      rank: 5,
      date: new Date(petition.debateInfoMeta?.addedAt || created),
      title: 'The parliamentary debate has been cancelled',
      body: di.notDebatedReason
        ? <p><span className="font-semibold">Reason for cancellation:</span> {di.notDebatedReason}</p>
        : null,
    });
  }

  if (di && di.status === 'not_debated') {
    events.push({
      key: 'not-debated',
      rank: 5,
      date: new Date(petition.debateInfoMeta?.addedAt || created),
      title: 'This petition will not be debated in Parliament',
      body: di.notDebatedReason
        ? <p><span className="font-semibold">Reason:</span> {di.notDebatedReason}</p>
        : null,
    });
  }

  if (petition.status === 'closed') {
    const closureText = petition.closureReason === 'term_ended'
      ? `This petition was closed because the parliamentary term ended.`
      : (petition.closureReason === 'three_weeks_insufficient_signatures'
        ? `This petition was closed as it did not reach the minimum of ${RESPOND_THRESHOLD} signatures within the time allowed.`
        : `This petition is now closed. It can no longer be signed.`);
    events.push({
      key: 'closed',
      rank: 10,
      date: new Date(petition.closureDate || closeDate),
      title: 'This petition is closed',
      body: <p>{closureText}</p>,
    });
  }

  events.sort((a, b) => (b.date - a.date) || ((b.rank || 0) - (a.rank || 0)));
  return events;
};

// Petition progress timeline
const PetitionProgress = ({ petition, signatureCount }) => {
  const events = buildProgressEvents(petition, signatureCount);
  return (
    <div data-testid="petition-progress">
      <h2 className="text-2xl font-bold text-gray-900 mb-1">Petition progress</h2>
      <p className="text-gray-600 mb-6">View all updates for this petition, with the most recent first.</p>
      <div className="relative">
        {events.map((ev, i) => (
          <div key={ev.key} className="relative pl-8 pb-8 last:pb-0">
            {i < events.length - 1 && (
              <span className="absolute left-[7px] top-4 bottom-0 w-px bg-gray-300" />
            )}
            <span className={`absolute left-0 top-1.5 w-4 h-4 rounded-full border-2 border-white ${i === 0 ? 'bg-blue-600' : 'bg-gray-300'}`} />
            <div className="font-bold text-gray-900 mb-2">{formatLongDate(ev.date)}</div>
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">{ev.title}</h3>
              <div className="text-sm text-gray-600">{ev.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Modal listing all signers (name links to ROBLOX profile)
const SignersModal = ({ signers, signatureCount, onClose }) => {
  const list = Array.isArray(signers) ? signers : [];
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="signers-modal"
    >
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Signatures ({(signatureCount || 0).toLocaleString()})</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" data-testid="close-signers-modal">×</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          {list.length === 0 ? (
            <p className="text-center text-gray-500 py-6">Be the first to sign this petition</p>
          ) : (
            list.map((signer, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <img
                  src={signer.avatar || `https://p01--petitionsavatar--sbsp69k9tyd5.code.run/avatar/${signer.id}`}
                  alt={signer.displayName || signer.username}
                  className="w-10 h-10 rounded-full bg-gray-200"
                  onError={(e) => { e.target.src = '/default-avatar.png'; }}
                />
                <div className="flex-1 min-w-0">
                  <a
                    href={`https://www.roblox.com/users/${signer.id}/profile`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {signer.displayName || signer.username}
                  </a>
                  {signer.signed_at && (
                    <div className="text-sm text-gray-500">
                      Signed {new Date(signer.signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const PetitionDetail = ({ petition, onBack, user, onSign }) => {
  const navigate = useNavigate();
  const [hasSignedPetition, setHasSignedPetition] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const [signatureCount, setSignatureCount] = useState(petition?.signatures || 0);
  const [signers, setSigners] = useState(petition?.signers || []);
  const [governmentResponse, setGovernmentResponse] = useState('');
  const [selectedGovernmentRole, setSelectedGovernmentRole] = useState('');
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [showDebateForm, setShowDebateForm] = useState(false);
  const [debateDate, setDebateDate] = useState('');
  const [debateLocation, setDebateLocation] = useState('Westminster Hall');
  const [debateStatus, setDebateStatus] = useState('scheduled');
  const [hansardLink, setHansardLink] = useState('');
  const [notDebatedReason, setNotDebatedReason] = useState('');
  const [showSignersModal, setShowSignersModal] = useState(false);

  // Check if user has signed this petition
  useEffect(() => {
    if (user && petition) {
      setHasSignedPetition(hasUserSignedPetition(user.id, petition));
      setSignatureCount(petition.signatures || 0);
      setSigners(petition.signers || []);
    }
  }, [user, petition]);

  // Listen for signature updates
  useEffect(() => {
    const handleSignatureUpdate = async () => {
      if (user && petition) {
        // Get updated petition data
        const allPetitions = await getAllPetitions();
        const updatedPetition = allPetitions.find(p => String(p.id) === String(petition.id));
        if (updatedPetition) {
          setHasSignedPetition(hasUserSignedPetition(user.id, updatedPetition));
          setSignatureCount(updatedPetition.signatures || 0);
          setSigners(updatedPetition.signers || []);
        }
      }
    };

    window.addEventListener('signaturesUpdated', handleSignatureUpdate);
    return () => window.removeEventListener('signaturesUpdated', handleSignatureUpdate);
  }, [user, petition]);

  // Handle signing/unsigning
  const handleSignature = async () => {
    if (!user) {
      alert('Please log in to sign this petition.');
      return;
    }

    if (!isBritishCitizen(user)) {
      alert('Only British Citizens can sign petitions.');
      return;
    }

    // Prevent double-clicking
    if (isSubmittingSignature) {
      return;
    }

    setIsSubmittingSignature(true);

    try {
      let result;
      if (hasSignedPetition) {
        result = await unsignPetition(petition.id, user);
      } else {
        result = await signPetition(petition.id, user);
      }

      if (result.success) {
        const newSignedState = !hasSignedPetition;
        setHasSignedPetition(newSignedState);
        setSignatureCount(result.newCount);
        
        // Fetch updated petition data to get the latest signers list
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}`);
          if (response.ok) {
            const updatedPetition = await response.json();
            setSigners(updatedPetition.signers || []);
            
            // Double-check the signed state based on actual signers list
            const userIsSigner = updatedPetition.signers.some(signer => String(signer.id) === String(user.id));
            setHasSignedPetition(userIsSigner);
          }
        } catch (fetchError) {
          console.error('Error fetching updated petition:', fetchError);
        }
        
        // Call parent callback if provided
        if (onSign) {
          onSign(petition.id, newSignedState);
        }
      } else {
        alert(result.error || 'Failed to process signature');
      }
    } catch (error) {
      console.error('Error handling signature:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  // Populate form when editing existing government response
  useEffect(() => {
    if (showResponseForm && petition.governmentResponse) {
      setGovernmentResponse(petition.governmentResponse);
      // Use the existing role from metadata if available, otherwise fallback to user's role
      if (petition.governmentResponseMeta && petition.governmentResponseMeta.addedByRole) {
        setSelectedGovernmentRole(petition.governmentResponseMeta.addedByRole);
      } else {
        setSelectedGovernmentRole('');
      }
    } else if (showResponseForm) {
      // Clear form when adding new response
      setGovernmentResponse('');
      setSelectedGovernmentRole('');
    }
  }, [showResponseForm, petition.governmentResponse, petition.governmentResponseMeta]);

  const handleRemoveGovernmentResponse = async () => {
    if (window.confirm('Are you sure you want to remove the government response? This action cannot be undone.')) {
      try {
        
        // Send Discord notification first
        sendPetitionEventWebhook('government_response_removed', petition, user, {
          removedResponse: petition.governmentResponse
        });

        // Call backend API to remove government response
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/government-response`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to remove government response');
        }

        // Update local petition data
        const updatedPetition = {
          ...petition,
          governmentResponse: null,
          governmentResponseMeta: null
        };
        Object.assign(petition, updatedPetition);
        
        alert('Government response removed successfully!');
        // Instead of reloading, dispatch an event to refresh data
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      } catch (error) {
        console.error('Error removing government response:', error);
        alert('Failed to remove government response. Please try again.');
      }
    }
  };

  const handleDeletePetition = async () => {
    if (!canDeletePetitions(user)) {
      alert('You do not have permission to delete petitions.');
      return;
    }

    const isConfirmed = window.confirm('Are you sure you want to delete this petition? This action cannot be undone.');
    
    if (isConfirmed) {
      try {
        // Use the proper deletePetition function with user info for logging
        const result = await deletePetition(petition.id, user);
        
        if (result.success) {
          // Send Discord notification on successful deletion
          sendPetitionEventWebhook('petition_deleted', petition, user)
            .catch(err => console.error('Discord notification error:', err));

          alert(result.message);
          onBack(); // Navigate back to petitions list
        } else {
          alert(result.message);
        }
      } catch (error) {
        console.error('Error deleting petition:', error);
        alert('Failed to delete petition. Please try again.');
      }
    }
  };

  const handleRemoveDebateInfo = async () => {
    if (window.confirm('Are you sure you want to remove the debate information? This action cannot be undone.')) {
      try {
        
        // Send Discord notification first
        sendPetitionEventWebhook('debate_removed', petition, user, {});

        // Call backend API to remove debate info
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/debate-info`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error('Failed to remove debate information');
        }

        // Update local petition data
        const updatedPetition = {
          ...petition,
          debateInfo: null,
          debateInfoMeta: null
        };
        Object.assign(petition, updatedPetition);
        
        alert('Debate information removed successfully!');
        // Instead of reloading, dispatch an event to refresh data
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      } catch (error) {
        console.error('Error removing debate information:', error);
        alert('Failed to remove debate information. Please try again.');
      }
    }
  };




  const [petitionTerm, setPetitionTerm] = useState(null);

  // Get the term associated with this petition
  useEffect(() => {
    const fetchPetitionTerm = async () => {
      if (petition && petition.termId) {
        try {
          const terms = await getParliamentaryTerms();
          const term = terms.find(t => String(t.id) === String(petition.termId));
          setPetitionTerm(term);
        } catch (error) {
          console.error('Error fetching petition term:', error);
        }
      }
    };
    
    fetchPetitionTerm();
  }, [petition]);

  const handleGovernmentResponse = async (e) => {
    e.preventDefault(); // Prevent form submission
    
    if (!governmentResponse.trim()) {
      alert('Please enter a government response');
      return;
    }

    if (!selectedGovernmentRole) {
      alert('Please select your government role');
      return;
    }

    try {
      // Send government response to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/government-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          petition_id: petition.id,
          response_text: governmentResponse,
          added_by: user.displayName || user.username,
          added_by_role: selectedGovernmentRole
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add government response');
      }

      // Send Discord notification
      const isEdit = petition.governmentResponse && petition.governmentResponse !== governmentResponse;
      
      if (isEdit) {
        // Send edit webhook
        sendPetitionEventWebhook('government_response_edited', petition, user, {
          oldResponse: petition.governmentResponse,
          newResponse: governmentResponse,
          selectedRole: selectedGovernmentRole
        });
      } else {
        // Send add webhook
        sendPetitionEventWebhook('government_response_added', petition, user, {
          response: governmentResponse,
          selectedRole: selectedGovernmentRole
        });
      }

      // Clear form
      setGovernmentResponse('');
      setSelectedGovernmentRole('');
      setShowResponseForm(false);
      
      alert('Government response added successfully!');
      
      // Trigger petition update to refresh the display
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      
    } catch (error) {
      console.error('Error adding government response:', error);
      alert('Failed to add government response. Please try again.');
    }
  };

  const handleDebateResponse = async (e) => {
    e?.preventDefault(); // Prevent form submission
    
    // Only require date for scheduled and debated petitions
    if ((debateStatus === 'scheduled' || debateStatus === 'debated') && !debateDate) {
      alert('Please enter a debate date');
      return;
    }

    // Validate required fields based on status
    if (debateStatus === 'debated' && !hansardLink.trim()) {
      alert('Please provide the Hansard link for the completed debate');
      return;
    }

    if (debateStatus === 'not_debated' && !notDebatedReason.trim()) {
      alert('Please provide a reason for not debating this petition');
      return;
    }

    if (debateStatus === 'cancelled' && !notDebatedReason.trim()) {
      alert('Please provide a reason for cancelling the debate');
      return;
    }

    // Prevent logout by not refreshing page
    try {
      // Create new debate info object
      const newDebateInfo = {
        date: (debateStatus === 'scheduled' || debateStatus === 'debated') ? debateDate : null,
        status: debateStatus,
        hansardLink: (debateStatus === 'debated' && hansardLink.trim()) ? hansardLink.trim() : null,
        notDebatedReason: ((debateStatus === 'not_debated' || debateStatus === 'cancelled') && notDebatedReason.trim()) ? notDebatedReason.trim() : null
      };

      // Save debate info to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petition.id}/debate-info`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          petition_id: petition.id,
          status: debateStatus,
          date: debateDate,
          location: debateLocation.trim() || 'Westminster Hall',
          hansard_url: (debateStatus === 'debated' && hansardLink.trim()) ? hansardLink.trim() : null,
          not_debated_reason: ((debateStatus === 'not_debated' || debateStatus === 'cancelled') && notDebatedReason.trim()) ? notDebatedReason.trim() : null,
          added_by: user.displayName || user.username,
          added_by_role: user.userType || 'Government Official'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save debate information');
      }

      // Send Discord notification for debate event
      const isEdit = petition.debateInfo !== null;
      
      if (isEdit) {
        sendPetitionEventWebhook('debate_edited', petition, user, {
          date: debateDate,
          status: debateStatus,
          location: debateLocation.trim() || 'Westminster Hall',
          hansardLink: hansardLink.trim() || null,
          notDebatedReason: notDebatedReason.trim() || null
        });
      } else {
        sendPetitionEventWebhook('debate_added', petition, user, {
          date: debateDate,
          status: debateStatus,
          location: debateLocation.trim() || 'Westminster Hall',
          hansardLink: hansardLink.trim() || null,
          notDebatedReason: notDebatedReason.trim() || null
        });
      }

      alert(`Debate information ${debateStatus === 'debated' ? 'completion' : 'scheduling'} submitted successfully!`);
      
      // Reset form
      setDebateDate('');
      setDebateLocation('Westminster Hall');
      setDebateStatus('scheduled');
      setHansardLink('');
      setNotDebatedReason('');
      setShowDebateForm(false);
      
      // Trigger petition update to refresh the display
      window.dispatchEvent(new CustomEvent('petitionsUpdated'));
      
    } catch (error) {
      console.error('Error saving debate information:', error);
      alert('Failed to save debate information. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white py-4 lg:py-6">
        <div className="container mx-auto px-4">
          <button
            onClick={onBack}
            className="text-blue-200 hover:text-white mb-2 text-sm lg:text-base"
          >
            ← Back to petitions
          </button>
          <div>
            {petitionTerm ? (
              <>
                <p className="text-blue-200 text-sm lg:text-base">This petition was submitted during {formatTermDisplay(petitionTerm)}</p>
                <p className="text-blue-200 text-sm lg:text-base">
                  <a 
                    href={`/petitions/term/${petitionTerm.id}`}
                    onClick={async (e) => {
                      e.preventDefault();
                      // Fetch petitions for this term
                      const termPetitions = await getPetitionsByTerm(petitionTerm.id);
                      if (termPetitions.length > 0) {
                        // Navigate to all petitions page with term filter
                        navigate(`/petitions?term=${petitionTerm.id}`);
                      } else {
                        alert(`No other petitions found for ${formatTermDisplay(petitionTerm)}`);
                      }
                    }}
                    className="text-blue-200 hover:text-white underline"
                  >
                    View other petitions from this parliamentary term
                  </a>
                </p>
              </>
            ) : (
              <p className="text-blue-200">Loading parliamentary term information...</p>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 lg:py-8 max-w-4xl">

        {/* Government Response Form */}
        {user && isGovernmentMinister(user) && (
          <div className="max-w-lg mx-auto mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-base lg:text-lg font-semibold mb-3 text-blue-800 flex items-center">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Government Admin Panel
            </h3>

            {/* Government Response Management */}
            <div className="mb-4">
              <h4 className="font-medium text-blue-800 mb-2">Government Response</h4>
              {petition.governmentResponse ? (
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm text-blue-600">
                      <strong>Current Response:</strong>
                      {petition.governmentResponseMeta && (
                        <div className="text-xs text-gray-600 mt-1">
                          Added by: <strong>{petition.governmentResponseMeta.addedBy}</strong>
                          <br />
                          Date: {new Date(petition.governmentResponseMeta.addedAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                          {petition.governmentResponseMeta.editedBy && (
                            <div className="mt-1">
                              Last edited by: <strong>{petition.governmentResponseMeta.editedBy}</strong> ({petition.governmentResponseMeta.editedByRole})
                              <br />
                              Date: {new Date(petition.governmentResponseMeta.editedAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canAddGovernmentResponse(user) && (
                        <button
                          onClick={() => setShowResponseForm(!showResponseForm)}
                          className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                        >
                          {petition.governmentResponse ? 'Edit' : 'Add'}
                        </button>
                      )}
                      {canRemoveGovernmentResponse(user) && (
                        <button
                          onClick={() => handleRemoveGovernmentResponse()}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    {typeof petition.governmentResponse === 'object' 
                      ? petition.governmentResponse.text || petition.governmentResponse.content || JSON.stringify(petition.governmentResponse)
                      : petition.governmentResponse
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">No government response yet.</p>
                  {canAddGovernmentResponse(user) && (
                    <button
                      onClick={() => setShowResponseForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                    >
                      Add Government Response
                    </button>
                  )}
                </div>
              )}
              
            </div>

            {/* Debate Info Management */}
            <div className="mb-4">
              <h4 className="font-medium text-blue-800 mb-2">Parliament Debate</h4>
              {petition.debateInfo ? (
                <div className="bg-white border border-blue-200 rounded p-3 mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm text-blue-600">
                      <strong>Current Debate Info:</strong>
                      {petition.debateInfoMeta && (
                        <div className="text-xs text-gray-600 mt-1">
                          Added by: <strong>{petition.debateInfoMeta.addedBy}</strong>
                          <br />
                          Date: {new Date(petition.debateInfoMeta.addedAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                          {petition.debateInfoMeta.editedBy && (
                            <div className="mt-1">
                              Last edited by: <strong>{petition.debateInfoMeta.editedBy}</strong> ({petition.debateInfoMeta.editedByRole})
                              <br />
                              Date: {new Date(petition.debateInfoMeta.editedAt).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canEditDebateInfo(user) && (
                        <button
                          onClick={() => setShowDebateForm(true)}
                          className="text-xs bg-yellow-600 text-white px-2 py-1 rounded hover:bg-yellow-700"
                        >
                          Edit
                        </button>
                      )}
                      {canRemoveDebateInfo(user) && (
                        <button
                          onClick={() => handleRemoveDebateInfo()}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    {typeof petition.debateInfo === 'object' 
                      ? new Date(petition.debateInfo.date).toLocaleDateString()
                      : petition.debateInfo.substring(0, 100) + '...'
                    }
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">No debate scheduled yet.</p>
                  {canEditDebateInfo(user) && (
                    <button
                      onClick={() => setShowDebateForm(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
                    >
                      Schedule Debate
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Admin Actions */}
            <div className="space-y-2">
              {canDeletePetitions(user) && (
                <button
                  onClick={handleDeletePetition}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  Delete Petition
                </button>
              )}

              {canManageTerms(user) && (
                <a
                  href="/term-management"
                  className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-center block"
                  onClick={(e) => {
                    e.preventDefault();
                    // Use React Router navigation instead of window.location
                    window.location.pathname = '/term-management';
                  }}
                >
                  Manage Parliamentary Terms
                </a>
              )}
            </div>

            {showResponseForm && (
              <div className="mt-4 p-3 bg-green-100 rounded">
                <h4 className="font-semibold mb-2 text-green-800">
                  {petition.governmentResponse ? 'Update Government Response' : 'Add Government Response'}
                </h4>
                <form onSubmit={handleGovernmentResponse}>
                  <textarea
                    value={governmentResponse}
                    onChange={(e) => setGovernmentResponse(e.target.value)}
                    placeholder="Enter the government's response to this petition..."
                    className="w-full p-2 border border-green-300 rounded"
                    rows={4}
                    required
                  />
                  
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      Select your government role *
                    </label>
                    <select
                      value={selectedGovernmentRole}
                      onChange={(e) => setSelectedGovernmentRole(e.target.value)}
                      className="w-full p-2 border border-green-300 rounded"
                      required
                    >
                      <option value="">-- Select your role --</option>
                      {GOVERNMENT_POSITIONS.map((position) => (
                        <option key={position} value={position}>
                          {position}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="mt-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 mr-2"
                    >
                    Submit Government Response
                  </button>
                  <button
                    onClick={() => setShowResponseForm(false)}
                    className="bg-gray-500 text-white px-4 py-1 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  </div>
                </form>
              </div>
            )}

            {showDebateForm && (
              <div className="mt-4 p-3 bg-purple-100 rounded">
                <h4 className="font-semibold mb-2 text-purple-800">
                  {petition.debateInfo ? 'Update Parliament Debate' : 'Add Parliament Debate'}
                </h4>
                <form onSubmit={handleDebateResponse}>
                  <div className="space-y-4">
                    {(debateStatus === 'scheduled' || debateStatus === 'debated') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Debate Date *</label>
                        <input
                          type="date"
                          value={debateDate}
                          onChange={(e) => setDebateDate(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Debate Status *</label>
                      <select
                        value={debateStatus}
                        onChange={(e) => setDebateStatus(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        required
                      >
                        <option value="scheduled">Scheduled</option>
                        <option value="debated">Debated</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="not_debated">Not debated in parliament</option>
                      </select>
                    </div>
                    
                    {debateStatus === 'debated' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hansard PDF Link *</label>
                        <input
                          type="url"
                          value={hansardLink}
                          onChange={(e) => setHansardLink(e.target.value)}
                          placeholder="https://hansard.parliament.uk/..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Till hansards return, use this link: https://drive.google.com/file/d/1vTXSZ6kTYalU6eaNyYq0DmIpe-92UgtS/view?usp=sharing
                        </p>
                      </div>
                    )}

                    {debateStatus === 'cancelled' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation *</label>
                        <textarea
                          value={notDebatedReason}
                          onChange={(e) => setNotDebatedReason(e.target.value)}
                          placeholder="Please provide the reason why this debate was cancelled..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          rows={3}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Explain why this parliamentary debate was cancelled
                        </p>
                      </div>
                    )}

                    {debateStatus === 'not_debated' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for not debating *</label>
                        <textarea
                          value={notDebatedReason}
                          onChange={(e) => setNotDebatedReason(e.target.value)}
                          placeholder="Please provide the reason why this petition will not be debated in parliament..."
                          className="w-full p-2 border border-gray-300 rounded-lg"
                          rows={3}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Explain why this petition will not receive a parliamentary debate
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowDebateForm(false)}
                      className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                    >
                      {debateStatus === 'debated' ? 'Record Completion' : debateStatus === 'scheduled' ? 'Schedule Debate' : 'Cancel Debate'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        <div className="p-4 lg:p-8">
          {/* Status badge */}
          <div className="mb-4">
            <span className={`inline-block px-3 py-1 rounded text-xs lg:text-sm font-medium ${
              petition.status === 'open' ? 'bg-green-100 text-green-800' :
              petition.status === 'closed' ? 'bg-red-100 text-red-800' :
              petition.status === 'awaiting_government_response' ? 'bg-blue-100 text-blue-800' :
              petition.status === 'government_response' ? 'bg-blue-100 text-blue-800' :
              petition.status === 'awaiting_debate_in_parliament' ? 'bg-purple-100 text-purple-800' :
              petition.status === 'debated_in_parliament' ? 'bg-purple-100 text-purple-800' :
              petition.status === 'not_debated_in_parliament' ? 'bg-gray-100 text-gray-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {petition.status === 'awaiting_government_response' ? 'Awaiting Government Response' :
               petition.status === 'government_response' ? 'Government Response' :
               petition.status === 'awaiting_debate_in_parliament' ? 'Awaiting Debate in Parliament' :
               petition.status === 'debated_in_parliament' ? 'Debated in Parliament' :
               petition.status === 'not_debated_in_parliament' ? 'Not Debated in Parliament' :
               petition.status.charAt(0).toUpperCase() + petition.status.slice(1)}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3">{petition.title}</h1>

          {/* Creator + creation date + tags */}
          <div className="mb-6 space-y-0.5">
            <p className="text-sm text-gray-600">Created by <span className="font-semibold text-gray-900">{petition.creator || 'Anonymous'}</span></p>
            <p className="text-sm text-gray-600">Created on <span className="font-semibold text-gray-900">{formatLongDate(petition.created || petition.createdAt)}</span></p>
            {petition.tags && petition.tags.length > 0 && (
              <p className="text-sm text-gray-600">Tags: <span className="font-semibold text-gray-900">{petition.tags.join(', ')}</span></p>
            )}
          </div>

          {/* Description */}
          <div className="prose max-w-none mb-6">
            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">{petition.description}</p>
          </div>

          {/* Sign button */}
          {petition.status !== 'closed' && (
            <button
              onClick={handleSignature}
              disabled={!user || !isBritishCitizen(user) || isSubmittingSignature}
              data-testid="sign-petition-button"
              className={`py-3 px-6 rounded-lg font-semibold text-sm lg:text-base ${
                !user || !isBritishCitizen(user) || isSubmittingSignature
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : hasSignedPetition
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {!user
                ? 'Login to sign this petition'
                : !isBritishCitizen(user)
                  ? 'Only British Citizens can sign petitions'
                  : isSubmittingSignature
                    ? (hasSignedPetition ? 'Removing signature...' : 'Signing...')
                    : hasSignedPetition
                      ? 'Remove signature'
                      : 'Sign this petition'
              }
            </button>
          )}

          {!user && petition.status === 'open' && (
            <p className="text-gray-600 text-sm mt-2">You must be logged in and be a British Citizen to sign this petition</p>
          )}
          {user && !isBritishCitizen(user) && petition.status === 'open' && (
            <p className="text-gray-600 text-sm mt-2">Only British Citizens can sign petitions</p>
          )}
          {petition.status === 'closed' && (
            <p className="text-gray-600 text-sm">This petition is closed. No more signatures can be added.</p>
          )}

          {/* Signature count */}
          <div className="mt-4">
            <div className="text-3xl font-bold text-gray-900" data-testid="signature-count">{signatureCount.toLocaleString()}</div>
            <div className="text-gray-600 text-sm">signatures</div>
          </div>

          {/* Progress bar */}
          {(() => {
            const progressMax = signatureCount >= RESPOND_THRESHOLD ? DEBATE_THRESHOLD : RESPOND_THRESHOLD;
            return (
              <div className="mt-3 mb-2">
                <div className="w-full bg-gray-200 rounded-sm h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-sm transition-all"
                    style={{ width: `${Math.min((signatureCount / progressMax) * 100, 100)}%` }}
                  />
                </div>
                <div className="flex justify-end text-sm text-gray-500 mt-1">
                  <span>{progressMax.toLocaleString()}</span>
                </div>
              </div>
            );
          })()}

          {/* View signatures */}
          <div className="mt-2">
            <button
              onClick={() => setShowSignersModal(true)}
              data-testid="view-signatures-link"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              View Signatures
            </button>
          </div>

          {/* Petition progress timeline */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <PetitionProgress petition={petition} signatureCount={signatureCount} />
          </div>
        </div>

        {showSignersModal && (
          <SignersModal
            signers={signers}
            signatureCount={signatureCount}
            onClose={() => setShowSignersModal(false)}
          />
        )}
      </div>
    </div>
  );
};
